import React, { RefObject } from "react"
import invariant from "tiny-invariant"
import { useRequiredContext } from '../utils/use-required-context'
import { ViewerConfig, type ViewProps } from "../components/Viewer"
import { getRepoName } from '../api/app-login'
import { RequestDataUpdate, requestDataUpdate } from '../shared/data-update/request-data-update'
import { RequestRdfUpload, requestRdfUpload } from '../shared/rdf-upload/request-rdf-upload'
import { RequestFileUpload, requestFileUpload } from '../shared/file-upload/request-file-upload'
import { WidgetSelectorHandle } from "@/components/editor/widget-selector/widget-selector"
import type { ConfigurationMode } from "@/components/editor/configuration-mode"

type InputAppCtx = Pick<
  ViewerConfig,
  'repoURL' | 'localRepoURL' | 'bbsparqlendpoint' | 'displayChromiumTranslateErrors'
>

export type WistorAppCtx = (
  | WistorBackendAppCtx
  | SparqlOnlyAppCtx
)

export type WistorBackendAppCtx = {
  type: 'wistor-backend'
  origin: string
  repoName: string | null
  urlSparql: string
  urlDownload: string
  urlUpload: string
  urlLogin: string
  urlIsLoggedIn: string
  urlAnonymousLogin: string
  makeUrl: (make: (ctx: WistorBackendAppCtx) => string) => string
  requestDataUpdate: RequestDataUpdate
  requestRdfUpload: RequestRdfUpload
  requestFileUpload: RequestFileUpload
  displayChromiumTranslateErrors: boolean
}

export type SparqlOnlyAppCtx = {
  type: 'sparql-only'
  urlSparql: string
  displayChromiumTranslateErrors: boolean
}

export type ViewerCtx = StandaloneViewerCtx | HostedViewerCtx

export type StandaloneViewerCtx = {
  host: "standalone"
  mode: "view"
  mainArea: ViewerConfig["mainArea"]
}

export type HostedViewerCtx = {
  host: "viewer"
  mode: ConfigurationMode
  viewProps: ViewProps
  mainArea: ViewerConfig["mainArea"]
  /** Applies a new main area to the viewer and returns the resulting config. */
  updateMainArea: (mainArea: ViewerConfig["mainArea"]) => ViewerConfig
  save: () => Promise<void>
  widgetSelectorRef: RefObject<WidgetSelectorHandle>
}

export const AppContext = React.createContext<WistorAppCtx | null>(null)
AppContext.displayName = "AppContext"

export const useAppCtx = () => useRequiredContext(AppContext)

export const ViewerContext = React.createContext<ViewerCtx | null>(null)
ViewerContext.displayName = "ViewerContext"

export const useViewerCtx = () => React.useContext(ViewerContext)

/** For editor UI which requires non-standalone context */
export function useHostedViewerCtx(): HostedViewerCtx {
  const viewerCtx = useViewerCtx()
  invariant(viewerCtx?.host === "viewer", "This can only be used inside a viewer")
  return viewerCtx
}

export function AppCtxProvider(props: InputAppCtx & {
  viewerCtx: ViewerCtx | null,
  children: React.ReactNode
}): React.ReactNode {
  const appCtx: WistorAppCtx = React.useMemo(() => getAppCtx({
    repoURL: props.repoURL,
    localRepoURL: props.localRepoURL,
    bbsparqlendpoint: props.bbsparqlendpoint,
    displayChromiumTranslateErrors: props.displayChromiumTranslateErrors,
  }), [props.repoURL, props.localRepoURL, props.bbsparqlendpoint, props.displayChromiumTranslateErrors])

  return (
    <AppContext.Provider value={appCtx}>
      <ViewerContext.Provider value={props.viewerCtx}>
        {props.children}
      </ViewerContext.Provider>
    </AppContext.Provider>
  )
}

export function getAppCtx(input: InputAppCtx): WistorAppCtx {
  const url = window.location.hostname === 'localhost' && input.localRepoURL
    ? new URL(input.localRepoURL, window.location.origin)
    : new URL(input.repoURL, window.location.origin)
  const repoName = getRepoName(input.bbsparqlendpoint)
  const isWistorEndpoint = url.pathname.startsWith('/servlets')

  if (!isWistorEndpoint) return {
    type: 'sparql-only',
    urlSparql: url.toString(),
    displayChromiumTranslateErrors: Boolean(input.displayChromiumTranslateErrors),
  }

  /* One spelling of the upload endpoint: urlUpload and requestFileUpload must not drift apart. */
  const urlUpload = `${url.origin}/servlets/cgi/upload/${repoName}`

  const ctx: WistorBackendAppCtx = {
    type: 'wistor-backend',
    origin: url.origin,
    repoName,
    urlSparql: `${url.origin}/servlets/cgi/sparql/${repoName}`,
    urlDownload: `${url.origin}/servlets/cgi/download/${repoName}`,
    urlUpload,
    urlLogin: getLoginUrl(repoName, url.toString(), url),
    urlAnonymousLogin: `${url.origin}/servlets/cgi/api/anon-login/${repoName}`,
    urlIsLoggedIn: `${url.origin}/servlets/cgi/isloggedin/${repoName}`,
    requestFileUpload: requestFileUpload(urlUpload),
    requestDataUpdate: requestDataUpdate(`${url.origin}/servlets/cgi/api/${repoName}/data-update`),
    requestRdfUpload: requestRdfUpload(`${url.origin}/servlets/cgi/api/${repoName}/rdf-upload`),
    makeUrl(make) { return make(ctx) },
    displayChromiumTranslateErrors: Boolean(input.displayChromiumTranslateErrors),
  }

  return ctx
}

export function getLoginUrl(repoName: string | null, repoUrl: string, urlOverride?: URL) {
  const url = urlOverride ?? new URL(repoUrl, window.location.origin)
  return `${url.origin}/servlets/cgi/login/${repoName}`
}
